import { money, money0, PROD, prodCorto, stageStyle, etapaLabelDe } from './helpers';

/*
 * Derivación del estado de cuenta: de lo que devuelve el backend
 * (getCredito + liquidacionPreview) al objeto que pintan la vista y el PDF.
 *
 * Vivía dentro de CarteraEstadosCuenta.jsx. Se extrajo cuando Conversaciones
 * necesitó generar el MISMO PDF para mandarlo por WhatsApp: duplicar estas
 * ~120 líneas habría garantizado que los dos documentos divergieran (uno
 * cobrando mora y el otro no, por ejemplo).
 */

export const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
export const MESES_LARGO = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

export const fmtDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y) return '—';
  return `${String(d).padStart(2, '0')} ${MESES[m - 1]} ${y}`;
};

// Mes con nombre completo: es el identificador del renglón del calendario, así
// que se lee "julio 2026" y no se confunde con el ordinal de la mensualidad.
export const mesLargo = (iso) => {
  if (!iso) return '—';
  const [y, m] = String(iso).slice(0, 10).split('-').map(Number);
  return `${MESES_LARGO[m - 1] || '—'} ${y}`;
};

export const hoyISO = () => new Date().toISOString().slice(0, 10);

// El calendario se identifica por MES CALENDARIO, no por el ordinal del contrato.
// El `periodo` que manda el backend en AP ("Mes 8/36") es el número de renta y
// AP032 arranca en dic‑2025 → "Mes 8" vence el 01‑jul‑2026: leerlo como agosto
// hacía ver un vencido legítimo como error. Título = mes de vencimiento;
// el ordinal queda como dato secundario ("cuota 8/36").
export const periodoTitulo = (row) => (row.fecha_vencimiento ? mesLargo(row.fecha_vencimiento) : (row.periodo || '—'));

// "cuota 8/36" (el /36 solo si conocemos el plazo). Fallback al label del backend
// si la fila no trae número.
export const cuotaTexto = (row, i, total) => {
  const n = row.numero ?? (i != null ? i + 1 : null);
  if (n == null) return row.periodo || '';
  return `cuota ${n}${total ? `/${total}` : ''}`;
};

// Una línea, para textos (correo/WhatsApp): "julio 2026 (cuota 8/36)".
export const periodoTexto = (row, i, total) => {
  const c = cuotaTexto(row, i, total);
  return c ? `${periodoTitulo(row)} (${c})` : periodoTitulo(row);
};

// Situación de una fila del calendario → etiqueta/color/restante para mostrar.
// `vencida` = venció y aún queda saldo (aplica también a cuotas con abono parcial).
// `restante` = lo que falta de ESA cuota (0 si está pagada).
export const filaEstatus = (row) => {
  const st = String(row.estatus || '').toLowerCase();
  // Condonada por recuperación de auto: ni se pagó ni se debe (restante 0, fuera
  // del vencido). Está cerrada para el calendario pero no cuenta como pagada.
  if (st === 'waived' || st === 'condonado') {
    return { key: 'condonada', label: row.condonada_recuperacion ? 'Condonada (recuperación)' : 'Condonada', c: '#7C3AED', b: '#EDE9FE', icon: 'pi-ban', restante: 0, vencida: false };
  }
  const pagado = st === 'pagado' || st === 'paid' || st === 'liquidado';
  const restante = row.restante != null ? Number(row.restante) : Math.max(0, Number(row.pago_total || 0) - Number(row.monto_pagado || 0));
  const pagadoAlgo = Number(row.monto_pagado || 0) > 0;
  const parcial = !pagado && restante > 0 && (st === 'pago_parcial' || st === 'partial' || pagadoAlgo);
  const vencida = !pagado && restante > 0 && !!row.fecha_vencimiento && String(row.fecha_vencimiento).slice(0, 10) < hoyISO();
  if (pagado) return { key: 'pagado', label: 'Pagado', c: '#00BA6E', b: '#E5F8EF', icon: 'pi-check-circle', restante: 0, vencida: false };
  // Abono parcial tiene prioridad de etiqueta (más informativo que "Vencido" a secas).
  if (parcial) return { key: 'parcial', label: vencida ? 'Parcial · vencido' : 'Abono parcial', c: '#F0961E', b: '#FDF1DF', icon: 'pi-money-bill', restante, vencida };
  if (vencida) return { key: 'vencido', label: 'Vencido', c: '#DC2626', b: '#FBE7E7', icon: 'pi-exclamation-circle', restante, vencida: true };
  return { key: 'pendiente', label: 'Pendiente', c: '#64748B', b: '#EEF1F4', icon: 'pi-circle', restante, vencida: false };
};

/**
 * doc = respuesta de getCredito ({ resumen, estado_de_cuenta, … }).
 * liquid = respuesta de liquidacionPreview (null en AP: el backend responde 422).
 * Devuelve null si todavía no hay documento.
 */
export const buildEdoCuentaView = (doc, liquid) => {
  if (!doc?.resumen) return null;
  const r = doc.resumen;
  const rows = doc.estado_de_cuenta || [];
  const prod = prodCorto(r.tipo);
  const isAP = prod === 'AP';

  const pagadas = rows.filter((x) => filaEstatus(x).key === 'pagado').length;
  const total = r.cuotas?.total || rows.length;
  const condonadas = rows.filter((x) => filaEstatus(x).key === 'condonada').length;
  const faltan = Math.max(0, total - pagadas - condonadas);
  // "Mes actual" = primera fila no pagada.
  const curIdx = rows.findIndex((x) => !['pagado', 'condonada'].includes(filaEstatus(x).key));
  const mesActual = curIdx >= 0 ? (rows[curIdx].numero ?? curIdx + 1) : '—';

  // Renta/pago mensual: la cuota real del calendario (refleja AP y AF/CS por
  // igual); si no hay filas, cae al campo renta_mensual del backend.
  const rentaMensual = (rows.find((x) => !['pagado', 'condonada'].includes(filaEstatus(x).key))?.pago_total) || (rows[0]?.pago_total) || r.renta_mensual || 0;

  // Vencido a la fecha = suma de lo PENDIENTE de cada cuota ya vencida (incluye
  // el remanente de cuotas con abono parcial). No es Σ cuota completa.
  let vencCount = 0;
  const vencidas = []; // { mes, restante, parcial } de cada cuota vencida
  const vencSaldo = rows.reduce((s, x, i) => {
    const f = filaEstatus(x);
    if (!f.vencida) return s;
    vencCount += 1;
    vencidas.push({ mes: periodoTexto(x, i, total), restante: f.restante, parcial: f.key === 'parcial' });
    return s + f.restante;
  }, 0);
  const mora = Number(r.mora_acumulada || 0);
  const hasVencido = vencSaldo > 0 || mora > 0;

  const finCards = isAP
    ? [
      { label: 'Renta mensual', value: money(rentaMensual), sub: `Día ${r.dia_de_pago || '—'} de cada mes`, icon: 'pi-calendar' },
      { label: 'Rentas pendientes', value: `${faltan} de ${total}`, sub: `${pagadas} pagadas`, icon: 'pi-clock' },
      r.residual != null
        ? { label: 'Valor residual', value: money0(r.residual), sub: 'Al término del plazo', icon: 'pi-tag' }
        : { label: 'Tasa anual', value: r.tasa != null ? `${Number(r.tasa).toFixed(2)}%` : '—', sub: 'Vigente', icon: 'pi-percentage' },
    ]
    : [
      { label: 'Pago mensual', value: money(rentaMensual), sub: `Día ${r.dia_de_pago || '—'} de cada mes`, icon: 'pi-calendar' },
      { label: 'Saldo capital', value: money0(r.saldo_insoluto), sub: `${pagadas} de ${total} pagadas`, icon: 'pi-wallet' },
      { label: 'Tasa anual', value: r.tasa != null ? `${Number(r.tasa).toFixed(2)}%` : '—', sub: 'Vigente', icon: 'pi-percentage' },
    ];

  const liqTotal = liquid?.total ?? liquid?.monto ?? null;
  const stage = stageStyle(r.etapa_cobranza, r.estatus);

  return {
    r, rows, prod, isAP, pagadas, condonadas, total, faltan, mesActual, curIdx, hasVencido, vencSaldo, vencCount, vencidas, mora,
    // Pivote para la ventana de impresión: la cuota actual (o la última si ya
    // todo está pagado). Al imprimir solo se muestran las filas cercanas.
    pivot: curIdx >= 0 ? curIdx : Math.max(0, rows.length - 1),
    finCards, rentaMensual,
    liqTotal, liqEstimada: liqTotal == null,
    liqShow: liqTotal != null ? money(liqTotal) : (r.saldo_insoluto != null ? money(r.saldo_insoluto) : '—'),
    liqDesc: isAP ? 'Valor residual + rentas devengadas' : 'Saldo capital + intereses devengados',
    stage, etapaLabel: etapaLabelDe(r.etapa_cobranza, r.estatus),
    folio: `EC-${r.clave_contrato}-${hoyISO().slice(2, 4)}${hoyISO().slice(5, 7)}`,
    vehiculo: r.vehiculo_detalle,
  };
};

/**
 * Objeto plano que consume EstadoCuentaPDF: incluye la VENTANA del calendario
 * (cuota actual ±4), no la tabla completa.
 */
export const buildEdoCuentaPdfData = (v, incluirMora = true) => {
  const win = v.rows
    .map((m, i) => ({ m, i }))
    .filter(({ i }) => Math.abs(i - v.pivot) <= 4);
  const rows = win.map(({ m, i }) => {
    const f = filaEstatus(m);
    return {
      n: String(m.numero ?? i + 1),
      mes: periodoTitulo(m),
      // El PDF es el documento formal: bajo el mes va el ordinal y el día exacto.
      sub: [cuotaTexto(m, i, v.total), m.fecha_vencimiento ? `vence ${fmtDate(m.fecha_vencimiento)}` : null].filter(Boolean).join(' · '),
      monto: f.key === 'parcial' ? money(f.restante) : money0(m.pago_total),
      montoColor: f.key === 'pagado' ? '#00BA6E' : (f.key === 'vencido' ? '#DC2626' : (f.key === 'parcial' ? '#E5631B' : '#0F172A')),
      estatus: f.label,
      color: f.c,
      nota: f.key === 'parcial' ? `de ${money0(m.pago_total)} · abonó ${money0(m.monto_pagado)}` : '',
      isCur: i === v.curIdx,
    };
  });
  const primero = win.length ? (win[0].m.numero ?? win[0].i + 1) : null;
  const ultimo = win.length ? (win[win.length - 1].m.numero ?? win[win.length - 1].i + 1) : null;
  const windowNote = win.length < v.rows.length ? `Mostrando cuotas ${primero}–${ultimo} de ${v.total} (alrededor de la cuota actual).` : '';
  const persona = v.r.tipo_persona === 'PM' ? 'Persona Moral' : (v.r.tipo_persona === 'PF' ? 'Persona Física' : null);
  return {
    fecha: fmtDate(hoyISO()),
    folio: v.folio,
    cliente: {
      nombre: v.r.cliente,
      linea1: [persona, v.r.rfc ? `RFC ${v.r.rfc}` : null].filter(Boolean).join('  ·  ') || v.r.tipo_label || '',
      linea2: `${v.r.tel ? `Tel ${v.r.tel}` : 'Sin teléfono registrado'}  ·  ${v.r.correo || 'Sin correo'}`,
    },
    contrato: {
      clave: v.r.clave_contrato,
      prodName: (PROD[v.prod] || PROD.AP).name,
      etapaLabel: v.etapaLabel, etapaColor: v.stage.color, etapaBg: v.stage.bg,
    },
    vehiculo: v.vehiculo ? {
      nombre: [v.vehiculo.marca, v.vehiculo.modelo].filter(Boolean).join(' '),
      anio: v.vehiculo.anio ? String(v.vehiculo.anio) : '', placas: v.vehiculo.placas || '', vin: v.vehiculo.vin || '',
    } : null,
    finCards: v.finCards,
    vencido: v.hasVencido ? {
      saldo: money(v.vencSaldo),
      mora: (incluirMora && v.mora > 0) ? money(v.mora) : null,
      total: money(v.vencSaldo + (incluirMora ? v.mora : 0)),
    } : null,
    calendarTitle: `Calendario de ${v.isAP ? 'rentas' : 'mensualidades'}`,
    tiles: { pagadas: String(v.pagadas), actual: String(v.mesActual), porPagar: String(v.faltan) },
    windowNote,
    rows,
    liquid: { estimada: v.liqEstimada, desc: v.liqDesc, value: v.liqShow },
    footer: `Estado de cuenta generado el ${fmtDate(hoyISO())}. Para aclaraciones: cobranza@peicar.com.mx. Montos en MXN incluyen IVA donde aplica.`,
  };
};
